<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { Dialog } from "@/components/Base/Headless";
import Button from "@/components/Base/Button";
import { FormInput, FormLabel, FormSelect } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import { useI18n } from "vue-i18n";
import Toastify from "toastify-js";
import { useDocumentListModalStore } from "@/stores/documentListModal";
import { documentListsApi, type DocumentListItem } from "@/modules/DocumentLists/api";

const { t } = useI18n();
const modalStore = useDocumentListModalStore();

const lists = ref<DocumentListItem[]>([]);
const selectedListId = ref<string>("");
const newListTitle = ref("");
const isLoadingLists = ref(false);
const isSubmitting = ref(false);

const isCreateNew = computed(() => selectedListId.value === "__new__");
const documentCount = computed(() => modalStore.documentIds.length);

const loadLists = async () => {
  isLoadingLists.value = true;
  try {
    const result = await documentListsApi.list({
      is_archived: false,
      size: 100,
    });
    lists.value = result.data;
  } catch (e) {
    console.error(e);
    lists.value = [];
  } finally {
    isLoadingLists.value = false;
  }
};

watch(
  () => modalStore.isOpen,
  (open) => {
    if (open) {
      selectedListId.value = "";
      newListTitle.value = "";
      loadLists();
    }
  }
);

const close = () => modalStore.close();

const submit = async () => {
  if (modalStore.documentIds.length === 0) return;

  if (!selectedListId.value) {
    Toastify({
      text: t("documentLists.modal.select_list"),
      style: { background: "#ef4444" },
    }).showToast();
    return;
  }

  isSubmitting.value = true;
  try {
    let targetListId: number;

    if (isCreateNew.value) {
      const title = newListTitle.value.trim();
      if (!title) {
        Toastify({
          text: t("documentLists.modal.title_required"),
          style: { background: "#ef4444" },
        }).showToast();
        return;
      }
      const created = await documentListsApi.create({ title });
      targetListId = created.id;
    } else {
      targetListId = Number(selectedListId.value);
    }

    const result = await documentListsApi.addDocuments(
      targetListId,
      modalStore.documentIds
    );

    const added = result.added ?? 0;
    const skipped = result.skipped ?? 0;

    if (added === 0) {
      Toastify({
        text: result.message || t("documentLists.modal.already_in_list"),
        style: { background: "#f59e0b" },
      }).showToast();
    } else {
      const text =
        documentCount.value > 1
          ? t("documentLists.modal.added_bulk_success", { added, skipped })
          : t("documentLists.modal.added_success");
      Toastify({ text, style: { background: "#10b981" } }).showToast();
      modalStore.notifySuccess();
      close();
    }
  } catch (e: any) {
    Toastify({
      text: e.response?.data?.message || t("common.error"),
      style: { background: "#ef4444" },
    }).showToast();
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog :open="modalStore.isOpen" @close="close">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="text-base font-medium">{{ $t("documentLists.modal.title") }}</h2>
      </Dialog.Title>
      <div class="p-5">
        <p v-if="documentCount > 0" class="text-sm text-slate-500 mb-4">
          {{
            documentCount === 1
              ? $t("documentLists.modal.one_document")
              : $t("documentLists.modal.documents_count", { count: documentCount })
          }}
        </p>
        <FormLabel>{{ $t("documentLists.modal.select_label") }}</FormLabel>
        <FormSelect
          v-model="selectedListId"
          class="w-full mt-2"
          :disabled="isLoadingLists"
        >
          <option value="">{{ $t("common.select") }}</option>
          <option v-for="list in lists" :key="list.id" :value="String(list.id)">
            {{ list.title }} ({{ list.documents_count }})
          </option>
          <option value="__new__">{{ $t("documentLists.modal.create_new") }}</option>
        </FormSelect>

        <div v-if="isCreateNew" class="mt-4">
          <FormLabel>{{ $t("documentLists.columns.title") }}</FormLabel>
          <FormInput
            v-model="newListTitle"
            type="text"
            class="w-full mt-2"
            :placeholder="$t('documentLists.modal.new_list_placeholder')"
          />
        </div>
      </div>
      <Dialog.Footer>
        <Button variant="outline-secondary" @click="close">
          {{ $t("common.cancel") }}
        </Button>
        <Button variant="primary" :disabled="isSubmitting" @click="submit">
          <Lucide
            v-if="isSubmitting"
            icon="Loader2"
            class="w-4 h-4 mr-2 animate-spin"
          />
          {{ $t("documentLists.modal.confirm") }}
        </Button>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
